import React from "react";
import { EditNewFormSection } from "components/MainSectionTemplate/types";
import { CurrencyAndExchangeRatePicker } from "components/exchangerate/CurrencyAndExchangeRatePicker";
import CompanyModalPicker from "components/Picker/CompanyPicker/CompanyModalPicker";
import CostCenterModalPicker from "components/Picker/CostCenterPicker/CostCenterModalPicker";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import OptionalTextAreaInput from "components/ValidatedFields/OptionalTextAreaInput";
import InvoiceSendDatePicker from "components/Invoice/InvoiceSendDatePicker";
import InvoiceDueDatePicker from "components/Invoice/InvoiceDueDatePicker";
import { PaymentTermInvoiceV1 } from "components/Invoice/PaymentTermInvoiceV1";
import { useInvoiceV1NewForm } from "invoice/hooks";
import { InvoiceNumber } from "../components/Invoice/InvoiceNumber";
import { CompanySmallDetails } from "components/SmallDetails";
import ArtistRidersDropdown from "components/dropdowns/reactselect/ArtistRiderDropdown";
import { Checkbox } from "components/Atoms/inputs/Checkbox";
import ChooseContact from "components/AddresseePicker/ChooseContact";
import { BankAccountDropdownOrWarning } from "./BankAccountDropdownOrWarning";
import BookkeepingPaginatedDropdown from "components/dropdowns/paginated/BookkeepingPaginatedDropdown";

type InvoiceV1NewFormHook = ReturnType<typeof useInvoiceV1NewForm>;

interface Config {
  title?: string;
  draftLabel?: string;
  displayProforma?: boolean;
  displayBookkeeping?: boolean;
  displayContact?: boolean;
  displayDocumentNumber?: {
    number: string;
    locked: server.dto.Locked;
  };
  displayBankAccount?: boolean;
  displayCostCenter?: boolean;
  displayArtistRider?: boolean;
  readOnlyAddressee?: boolean;
  alwaysShowCurrency?: boolean;
}

const defaultConfig: Required<Config> = {
  title: site.currentEnvironment?.invoice.newLabel,
  draftLabel: RESX.Document.ProForma,
  displayProforma: true,
  displayBookkeeping: true,
  displayDocumentNumber: undefined,
  displayBankAccount: true,
  displayCostCenter: true,
  readOnlyAddressee: false,
  displayContact: true,
  displayArtistRider: false,
  alwaysShowCurrency: false,
}

export const getInvoiceV1NewSections = (hook: InvoiceV1NewFormHook, initialConfig?: Config): EditNewFormSection => {
  const config = {
    ...defaultConfig,
    ...initialConfig,
  };

  return {
    key: "invoiceNew",
    type: "regular",
    title: config.title,
    fields: [
      {
        key: "draft",
        label: config.draftLabel,
        hidden: !config.displayProforma,
        renderEditNew: () => (
          <div className="main-section-template-fields-padding" style={{ paddingLeft: 0 }}>
            <Checkbox
              label={""}
              checked={hook.isDraft}
              onChange={(_, checked) => hook.setIsDraft(checked)}
            />
          </div>
        )
      }, {
        key: "invoiceNumber",
        label: RESX.GeneralLabels.Number,
        hidden: !config.displayDocumentNumber,
        renderEditNew: () => (
          <div className="main-section-template-fields-padding">
            <InvoiceNumber
              invoiceNumber={config?.displayDocumentNumber?.number}
              locked={config?.displayDocumentNumber?.locked}
            />
          </div>
        )
      }, {
        key: "bookkeeping",
        label: RESX.Bookkeeping.resxBookkeeping,
        hidden: !config.displayBookkeeping,
        renderEditNew: () => (
          <BookkeepingPaginatedDropdown
            value={hook.bookkeeping}
            onChange={hook.setBookkeeping}
            selectIfOnlyOption
            config={{ validatorKey: "bookkeeping" }}
          />
        )
      },
      {
        key: "currencyAndExchangeRate",
        label: RESX.Financial.Currency,
        // Visible if: alwaysShowCurrency is true OR bookkeeping is selected and displayBookkeeping is true
        hidden: !config.alwaysShowCurrency && (hook.bookkeeping == null || !config.displayBookkeeping),
        renderEditNew: () => (
          <CurrencyAndExchangeRatePicker
            validatorKey="currencyAndExchangeRate"
            bookkeeping={hook.bookkeeping}
            currency={hook.currency}
            exchangeRate={hook.exchangeRate}
            onChange={(v) => {
              hook.setCurrency(v.currency);
              hook.setExchangeRate(v.exchangeRate);
            }}
            onEsc={() => { }}
            onEnter={() => { }}
          />
        )
      },
      {
        key: "bankAccount",
        hidden: hook.bookkeeping == null || !config.displayBookkeeping || !config.displayBankAccount,
        label: RESX.BankAccount.resxBankAccount,
        renderEditNew: () => {
          return (
            <BankAccountDropdownOrWarning
              bankAccountQuery={hook.bankAccountsApi}
              bookkeepingId={hook.bookkeeping?.bookkeepingId}
              value={hook.bankAccount}
              onChange={hook.setBankAccount}
            />
          );
        }
      },
      {
        key: "addressee",
        label: RESX.Document.Addressee,
        hidden: config.readOnlyAddressee,
        renderEditNew: () => (
          <div className="main-section-template-fields-padding">
            <CompanyModalPicker
              value={hook.addressee}
              onChange={hook.onAddresseeChange}
              modalTitle={RESX.Company.resxCompany}
              addressType="administrative"
              validatorKey="addressee"
              required
              canCreateNew={site.permissions.addressBook.canCreate}
              layout="multiLine"
            />
          </div>
        )
      }, {
        key: "readonlyAddressee",
        label: RESX.Document.Addressee,
        hidden: !config.readOnlyAddressee,
        renderEditNew: () => (
          <div className="main-section-template-fields-padding">
            <CompanySmallDetails company={hook.addressee} layout="multiLine" />
          </div>
        )
      },
      {
        key: "contact",
        label: RESX.Contact.resxContact,
        hidden: !config.displayContact || hook.addressee == null,
        renderEditNew: () => (
          <div className="main-section-template-fields-padding">
            <ChooseContact
              company={hook.addressee}
              selectedContact={hook.contact}
              onContactChanged={hook.setContact}
            />
          </div>
        )
      },
      {
        key: "sendDate",
        label: RESX.DateTime.SendDate,
        renderEditNew: () => (
          <InvoiceSendDatePicker
            value={hook.invoiceDate}
            onChange={hook.setSendDate}
            dueInDays={hook.dueInDays}
          />
        )
      },
      {
        key: "dueDate",
        label: RESX.DateTime.DueDate,
        renderEditNew: () => (
          <InvoiceDueDatePicker
            sendDate={hook.invoiceDate}
            dueInDays={hook.dueInDays}
            onChange={(_, dueInDays) => hook.setDueInDays(dueInDays)}
          />
        )
      },
      {
        key: "paymentTerm",
        label: RESX.DateTime.Term,
        renderEditNew: () => (
          <div className="main-section-template-fields-padding">
            <PaymentTermInvoiceV1 dueInDays={hook.dueInDays} />
          </div>
        )
      },
      {
        key: "concerning",
        label: RESX.InvoiceDocument.Concerning,
        renderEditNew: () => (
          <OptionalTextAreaInput
            maxLength={serverReference.constants.document.maxConcerningLength}
            value={hook.concerning}
            onChange={hook.setConcerning}
          />
        )
      },
      {
        key: "costCenter",
        label: RESX.CostCenter.resxCostCenter,
        hidden: !site.currentEnvironment.hasCostCenters || !config.displayCostCenter,
        renderEditNew: () => (
          <div className="main-section-template-fields-padding">
            <CostCenterModalPicker
              canCreateNew={false}
              value={hook.costCenter}
              onChange={hook.setCostCenter}
              validatorKey="costCenter"
              required
            />
          </div>
        )
      },
      {
        key: "artistRider",
        label: RESX.ArtistRider.resxArtistRider,
        hidden: !config.displayArtistRider || hook.allArtistRiders.length === 0,
        renderEditNew: () => (
          <ArtistRidersDropdown
            value={hook.artistRider}
            onChange={hook.setArtistRider}
            options={hook.allArtistRiders}
          />
        )
      },
      {
        key: "owner",
        label: RESX.GeneralLabels.Owner,
        renderEditNew: () => (
          <div className="main-section-template-fields-padding">
            <UserModalPicker
              value={hook.owner}
              onChange={hook.setOwner}
              validatorKey="owner"
              required
              canCreateNew={false}
            />
          </div>
        )
      }
    ]
  }
};

